import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { rpc } from "../../lib/rpc";
import { uuid4 } from "../../hooks/useFlowOperationId";
import { AdminPageHeader, DataTable } from "../../components/admin/DataTable";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

const STATUS_LABELS = {
    open: "offen",
    in_progress: "reserviert",
    completed: "abgeschlossen",
    cancelled: "abgebrochen",
};

const emptyPosition = () => ({ key: uuid4(), articleId: "", quantity: "1" });
const emptyErrors = () => ({ project: "", positions: [], server: "" });

export default function AdminPicklisten() {
    const [list, setList] = useState([]);
    const [articles, setArticles] = useState([]);
    const [showCreate, setShowCreate] = useState(false);

    const load = () =>
        Promise.all([rpc.get("/picklists"), rpc.get("/master/articles")]).then(
            ([picklistsResult, articlesResult]) => {
                if (picklistsResult.ok) setList(picklistsResult.data);
                else toast.error(picklistsResult.error);
                if (articlesResult.ok) {
                    setArticles(
                        articlesResult.data
                            .filter((article) => article.lifecycle === "active")
                            .sort((a, b) => a.name.localeCompare(b.name, "de"))
                    );
                } else {
                    toast.error(articlesResult.error);
                }
            }
        );
    useEffect(() => { load(); }, []);

    const reserve = async (id) => {
        const res = await rpc.post(`/picklists/${id}/reserve`, { client_operation_id: uuid4() });
        if (!res.ok) return toast.error(res.error);
        toast.success("Reserviert.");
        load();
    };
    // R28: Abschließen bucht die angeforderten Mengen als Entnahme
    // (abweichende gepickte Mengen erfasst das Team im mobilen Flow).
    const complete = async (id) => {
        const res = await rpc.post(`/picklists/${id}/complete`, { client_operation_id: uuid4() });
        if (!res.ok) return toast.error(res.error);
        toast.success("Pickliste abgeschlossen — angeforderte Mengen gebucht.");
        load();
    };

    const columns = [
        { key: "project_name", label: "Projekt", render: (r) => r.project_name || r.id.slice(0, 8) },
        { key: "status", label: "Status", render: (r) => STATUS_LABELS[r.status] || r.status },
        { key: "created_at", label: "Erstellt", render: (r) => new Date(r.created_at).toLocaleDateString("de-DE") },
        {
            key: "actions", label: "Aktionen", sortable: false,
            render: (r) => (
                <div className="flex gap-2">
                    {r.status === "open" ? (
                        <Button variant="outline" size="sm" onClick={() => reserve(r.id)} data-testid={`pl-reserve-${r.id}`}>
                            Reservieren
                        </Button>
                    ) : null}
                    {r.status === "in_progress" ? (
                        <Button size="sm" onClick={() => complete(r.id)} data-testid={`pl-complete-${r.id}`}>
                            Abschließen
                        </Button>
                    ) : null}
                </div>
            )
        },
    ];
    return (
        <>
            <AdminPageHeader title="Picklisten" subtitle={`${list.length} Pickliste(n)`}>
                <Button className="h-11 text-slate-950" onClick={() => setShowCreate(true)} data-testid="picklist-new">
                    <Plus aria-hidden="true" className="h-4 w-4" /> Neue Pickliste
                </Button>
            </AdminPageHeader>
            <DataTable testId="admin-picklisten-table" columns={columns} rows={list} rowKey={(r) => r.id} />
            <CreatePickListDialog
                open={showCreate}
                articles={articles}
                onClose={() => setShowCreate(false)}
                onCreated={() => {
                    setShowCreate(false);
                    load();
                }}
            />
        </>
    );
}

function CreatePickListDialog({ open, articles, onClose, onCreated }) {
    const [project, setProject] = useState("");
    const [positions, setPositions] = useState(() => [emptyPosition()]);
    const [errors, setErrors] = useState(emptyErrors);
    const [busy, setBusy] = useState(false);
    // Bei einem Fehler behält der Dialog die zuletzt quittierte Position.
    // Ein Retry wiederholt dadurch keine bereits bestätigten API-Schritte.
    const [createdListId, setCreatedListId] = useState(null);
    const [nextPositionIndex, setNextPositionIndex] = useState(0);

    const reset = () => {
        setProject("");
        setPositions([emptyPosition()]);
        setErrors(emptyErrors());
        setBusy(false);
        setCreatedListId(null);
        setNextPositionIndex(0);
    };

    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };

    const updatePosition = (index, field, value) => {
        setPositions((current) =>
            current.map((position, positionIndex) =>
                positionIndex === index ? { ...position, [field]: value } : position
            )
        );
        setErrors((current) => ({ ...current, positions: [], server: "" }));
    };

    const addPosition = () => {
        setPositions((current) => [...current, emptyPosition()]);
        setErrors((current) => ({ ...current, positions: [], server: "" }));
    };

    const removePosition = (index) => {
        setPositions((current) => current.filter((_, positionIndex) => positionIndex !== index));
        setErrors((current) => ({ ...current, positions: [], server: "" }));
    };

    const validate = () => {
        const positionErrors = positions.map(() => ({ article: "", quantity: "" }));
        const seenArticles = new Set();
        let valid = true;
        positions.forEach((position, index) => {
            if (!position.articleId) {
                positionErrors[index].article = "Bitte einen Artikel auswählen.";
                valid = false;
            } else if (seenArticles.has(position.articleId)) {
                positionErrors[index].article = "Dieser Artikel ist bereits in der Pickliste.";
                valid = false;
            } else {
                seenArticles.add(position.articleId);
            }
            const quantity = Number(position.quantity);
            if (!Number.isInteger(quantity) || quantity < 1) {
                positionErrors[index].quantity = "Bitte eine ganze Menge ab 1 eingeben.";
                valid = false;
            }
        });
        const projectError = project.trim() ? "" : "Pflichtfeld: Bitte ein Projekt eingeben.";
        if (projectError) valid = false;
        setErrors({ project: projectError, positions: positionErrors, server: "" });
        return valid;
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!createdListId && !validate()) return;

        setBusy(true);
        setErrors((current) => ({ ...current, server: "" }));
        let listId = createdListId;
        let startAt = nextPositionIndex;

        if (!listId) {
            const created = await rpc.post("/picklists", { project_name: project.trim() });
            if (!created.ok) {
                setErrors((current) => ({ ...current, server: created.error }));
                setBusy(false);
                return;
            }
            listId = created.data.id;
            startAt = 0;
            setCreatedListId(listId);
        }

        for (let index = startAt; index < positions.length; index += 1) {
            const item = await rpc.post(`/picklists/${listId}/items`, {
                article_id: positions[index].articleId,
                requested_quantity: Number(positions[index].quantity),
            });
            if (!item.ok) {
                setNextPositionIndex(index);
                setErrors((current) => ({
                    ...current,
                    server: `Position ${index + 1} konnte nicht gespeichert werden: ${item.error} Erneut versuchen setzt hier fort.`,
                }));
                setBusy(false);
                return;
            }
            setNextPositionIndex(index + 1);
        }

        const reserved = await rpc.post(`/picklists/${listId}/reserve`, {
            client_operation_id: uuid4(),
        });
        if (!reserved.ok) {
            setErrors((current) => ({
                ...current,
                server: `Die Pickliste ist angelegt, aber noch nicht reserviert: ${reserved.error}`,
            }));
            setBusy(false);
            return;
        }

        toast.success("Pickliste angelegt und reserviert.");
        reset();
        onCreated();
    };

    const selectedArticleIds = new Set(positions.map((position) => position.articleId));
    const locked = Boolean(createdListId);

    return (
        <Dialog open={open} onOpenChange={(value) => !value && close()}>
            <DialogContent
                className="max-h-[90vh] max-w-3xl overflow-y-auto"
                data-testid="picklist-create-dialog"
                onEscapeKeyDown={(event) => busy && event.preventDefault()}
                onPointerDownOutside={(event) => busy && event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>Neue Pickliste</DialogTitle>
                    <DialogDescription className="text-foreground/75">
                        Projekt und Artikelpositionen erfassen. Die fertige Liste wird direkt für den mobilen Pick-Flow reserviert.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} noValidate className="flex flex-col gap-5">
                    {errors.server ? (
                        <Alert
                            variant="destructive"
                            className="text-red-700 dark:text-red-300"
                            data-testid="picklist-create-error"
                        >
                            <AlertDescription>{errors.server}</AlertDescription>
                        </Alert>
                    ) : null}

                    <div className="space-y-2">
                        <Label htmlFor="picklist-project">Projekt</Label>
                        <Input
                            id="picklist-project"
                            className="h-11"
                            autoFocus
                            disabled={busy || locked}
                            value={project}
                            onChange={(event) => {
                                setProject(event.target.value);
                                setErrors((current) => ({ ...current, project: "", server: "" }));
                            }}
                            aria-invalid={Boolean(errors.project)}
                            aria-describedby={errors.project ? "picklist-project-error" : undefined}
                            data-testid="picklist-project"
                        />
                        {errors.project ? (
                            <p
                                id="picklist-project-error"
                                className="text-sm text-destructive dark:text-red-300"
                                data-testid="picklist-project-error"
                            >
                                {errors.project}
                            </p>
                        ) : null}
                    </div>

                    <div className="space-y-3">
                        <div>
                            <h3 className="font-semibold">Positionen</h3>
                            <p className="text-sm text-foreground/75">Jeden Artikel einmal mit der benötigten Menge hinzufügen.</p>
                        </div>
                        {articles.length === 0 ? (
                            <Alert>
                                <AlertDescription>Es sind keine aktiven Artikel verfügbar.</AlertDescription>
                            </Alert>
                        ) : null}
                        {positions.map((position, index) => {
                            const articleError = errors.positions[index]?.article;
                            const quantityError = errors.positions[index]?.quantity;
                            return (
                                <fieldset key={position.key} className="rounded-lg border border-border p-4">
                                    <legend className="px-1 text-sm font-semibold">Position {index + 1}</legend>
                                    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_9rem_auto] md:items-start">
                                        <div className="space-y-2">
                                            <Label htmlFor={`picklist-article-${index}`}>Artikel</Label>
                                            <Select
                                                value={position.articleId}
                                                disabled={busy || locked}
                                                onValueChange={(value) => updatePosition(index, "articleId", value)}
                                            >
                                                <SelectTrigger
                                                    id={`picklist-article-${index}`}
                                                    className="h-11"
                                                    aria-invalid={Boolean(articleError)}
                                                    aria-describedby={articleError ? `picklist-article-${index}-error` : undefined}
                                                    data-testid={`picklist-article-${index}`}
                                                >
                                                    <SelectValue placeholder="Artikel auswählen" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {articles.map((article) => (
                                                        <SelectItem
                                                            key={article.id}
                                                            value={article.id}
                                                            disabled={selectedArticleIds.has(article.id) && article.id !== position.articleId}
                                                        >
                                                            {article.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {articleError ? (
                                                <p
                                                    id={`picklist-article-${index}-error`}
                                                    className="text-sm text-destructive dark:text-red-300"
                                                    data-testid={`picklist-article-${index}-error`}
                                                >
                                                    {articleError}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor={`picklist-quantity-${index}`}>Menge</Label>
                                            <Input
                                                id={`picklist-quantity-${index}`}
                                                className="h-11"
                                                type="number"
                                                inputMode="numeric"
                                                min="1"
                                                step="1"
                                                disabled={busy || locked}
                                                value={position.quantity}
                                                onChange={(event) => updatePosition(index, "quantity", event.target.value)}
                                                aria-invalid={Boolean(quantityError)}
                                                aria-describedby={quantityError ? `picklist-quantity-${index}-error` : undefined}
                                                data-testid={`picklist-quantity-${index}`}
                                            />
                                            {quantityError ? (
                                                <p
                                                    id={`picklist-quantity-${index}-error`}
                                                    className="text-sm text-destructive dark:text-red-300"
                                                >
                                                    {quantityError}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="pt-7">
                                            {positions.length > 1 ? (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-11 w-11"
                                                    disabled={busy || locked}
                                                    onClick={() => removePosition(index)}
                                                    aria-label={`Position ${index + 1} entfernen`}
                                                    data-testid={`picklist-remove-${index}`}
                                                >
                                                    <Trash2 aria-hidden="true" />
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                </fieldset>
                            );
                        })}
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 self-start"
                            disabled={busy || locked || articles.length === 0}
                            onClick={addPosition}
                            data-testid="picklist-add-position"
                        >
                            <Plus aria-hidden="true" /> Position hinzufügen
                        </Button>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" className="h-11" disabled={busy} onClick={close}>
                            Abbrechen
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 text-slate-950"
                            disabled={busy || articles.length === 0}
                            data-testid="picklist-create-submit"
                        >
                            {busy ? "Wird verarbeitet …" : locked ? "Fortsetzen & reservieren" : "Anlegen & reservieren"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
